import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { geocodeAddress, parseLatLng, reverseGeocode } from "../lib/geocode";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CopySimple, MapPinSimple, Plus, Trash, ImagesSquare, CaretDown, Warning, Prohibit } from "@phosphor-icons/react";
import { ExpandablePinPicker } from "./FullscreenPinPicker";
import MenuImagesEditor from "./MenuImagesEditor";
import FlyerExtractCard from "./FlyerExtractCard";
import { FOOD_CATEGORIES, getCategoryOrDefault } from "../lib/foodCategories";
import WeekSelector, { defaultPlanningWeekStart } from "./WeekSelector";
import EventEditDrawer from "./EventEditDrawer";
import { fmtET, etTimeMs, etDateKey, etWeekdayIndex } from "../lib/etTime";

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

// Cancellation reason options — kept in sync with backend CANCEL_REASONS
// and the matching list in Dashboard.jsx. Order intentionally matches the
// spec the owner reads top-to-bottom.
const CANCEL_REASONS = [
  { value: "weather", label: "Weather" },
  { value: "mechanical", label: "Mechanical Issue" },
  { value: "staffing", label: "Staffing Shortage" },
  { value: "organizer", label: "Event Organizer Canceled" },
  { value: "personal", label: "Personal Emergency" },
  { value: "other", label: "Other" },
];

// Truck Calendar operational metadata (Feb 27 2026 — Phase 1A).
// `visibility` is the only field that affects the customer map.
// Everything else is owner-only ops detail and will become staff-shareable
// in Phase 1B.
const VISIBILITY_OPTIONS = [
  { value: "public",  label: "Public",   hint: "Appears on the customer map (default).",
    accent: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { value: "staff",   label: "Staff",    hint: "Hidden from customers. Staff + owner only.",
    accent: "border-indigo-300 bg-indigo-50 text-indigo-700" },
  { value: "private", label: "Private",  hint: "Owner-only. Prep day, training, internal.",
    accent: "border-slate-300 bg-slate-50 text-slate-700" },
];

const EVENT_TYPE_OPTIONS = [
  { value: "street",         label: "Street / Lot" },
  { value: "festival",       label: "Festival" },
  { value: "catering",       label: "Catering" },
  { value: "private_party",  label: "Private Party" },
  { value: "popup",          label: "Pop-up / Promo" },
  { value: "farmers_market", label: "Farmers Market" },
  { value: "brewery",        label: "Brewery / Taproom" },
  { value: "corporate",      label: "Corporate Booking" },
  { value: "other",          label: "Other" },
];

function emptyEntry() {
  return {
    event_name: "",
    address: "",
    lat: null,
    lng: null,
    starts_time: "11:00",
    ends_time: "14:00",
    menu_image_paths: [],
    recurring: false,
    // Per-entry cancellation. Matches the backend ScheduleEntry triplet
    // — this stop's pin paints red on the customer map (not erased)
    // when toggled on. The fields persist via the normal "Save this
    // week's schedule" button alongside everything else.
    is_canceled: false,
    cancel_reason: null,
    cancel_reason_custom: "",
    // Truck Calendar operations (Phase 1A). `visibility` is the only
    // field that hits the customer feed; the rest are owner-side ops
    // metadata that ride along on the entry. Default to "public" so
    // adding a new stop is backwards-compatible with every existing
    // workflow — owners opt in to private/staff explicitly.
    visibility: "public",
    event_type: null,
    prep_count: null,
    prep_notes: "",
    staff_needed: null,
    ops_notes: "",
  };
}

function emptySchedule() {
  return {
    truck_name: "",
    menu_text: "",
    menu_image_paths: [],
    food_category: "default",
    days: Object.fromEntries(DAYS.map((d) => [d.key, []])),
  };
}

/**
 * GcalChip — read-only summary tile for Google-Calendar-synced events,
 * with an inline "Fix pin" button that opens the existing
 * ExpandablePinPicker so the owner can drag the marker on the map
 * (much more accurate than re-typing the address when the geocoder is
 * already wrong). Real reports: rural Lancaster County addresses like
 * "877 Martin Church Rd" falling back to the town centroid — the
 * correct fix is "click on the map where the truck actually parks."
 * The override persists across syncs; the event title/times still flow
 * from GCal so calendar edits work.
 */
function GcalChip({ p, isGcal, dKey, fmtChipTime, onChanged }) {
  const [editing, setEditing] = useState(false);
  // Slide-out full edit drawer state. When true, opens the
  // EventEditDrawer with this row pre-populated. Fixes the user
  // complaint "for those who have synced gcal, there's no way to
  // edit those events" — GCal-origin rows are now fully editable
  // like any manual one-off, with two-way sync back to Google.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Local pin state so the map can be dragged BEFORE the user hits Save.
  // Seeded from the row's current lat/lng (or pin_override if present).
  const [pin, setPin] = useState({
    lat: p.pin_override?.lat ?? p.lat ?? null,
    lng: p.pin_override?.lng ?? p.lng ?? null,
  });
  const [busy, setBusy] = useState(false);
  const hasOverride = !!p.pin_override;

  // Cancel/Uncancel inline panel state. Mirrors the "Fix pin" pattern so
  // owners can mark a single event canceled without leaving the Schedule
  // tab. Works for both GCal-synced and one-off chips — cancel fields
  // aren't part of the GCal sync overwrite set so they survive re-syncs.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelForm, setCancelForm] = useState({
    is_canceled: !!p.is_canceled,
    cancel_reason: p.cancel_reason || null,
    cancel_reason_custom: p.cancel_reason_custom || "",
  });
  const [cancelBusy, setCancelBusy] = useState(false);

  const save = async () => {
    if (pin.lat == null || pin.lng == null) {
      toast.error("Drop a pin on the map first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/owner/gcal-events/${p.id}/override-pin`, {
        lat: pin.lat,
        lng: pin.lng,
      });
      toast.success("Pin updated", {
        description: "Future GCal syncs will keep this corrected location.",
      });
      setEditing(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't update the pin");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/owner/gcal-events/${p.id}/override-pin`);
      toast.success("Reset — next sync will re-geocode the GCal address.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't reset");
    } finally {
      setBusy(false);
    }
  };

  // PUT /api/trucks/{id} with just the cancel triplet. The backend
  // treats omitted fields as "no change" for non-cancel attributes,
  // so this is safe for GCal-synced events whose title/address/times
  // would otherwise be overwritten on next sync.
  const saveCancel = async () => {
    setCancelBusy(true);
    try {
      const payload = {
        is_canceled: !!cancelForm.is_canceled,
        cancel_reason: cancelForm.is_canceled ? (cancelForm.cancel_reason || null) : null,
        cancel_reason_custom: (cancelForm.is_canceled && cancelForm.cancel_reason === "other")
          ? (cancelForm.cancel_reason_custom || "").trim() || null
          : null,
      };
      await api.put(`/trucks/${p.id}`, payload);
      toast.success(payload.is_canceled ? "Event marked canceled" : "Event uncanceled");
      setCancelOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't update");
    } finally {
      setCancelBusy(false);
    }
  };

  // Promote this GCal-synced event to a recurring weekly schedule entry
  // (Feb 2026). After conversion, future GCal pins for the same series
  // are silently hidden so the customer map shows one pin, not two.
  // Idempotent on the server — re-tapping is a no-op.
  const [convertBusy, setConvertBusy] = useState(false);
  const convertToWeekly = async () => {
    const dayLabel = (DAYS.find((x) => x.key === dKey) || {}).label || "this weekday";
    if (!window.confirm(`Make every ${dayLabel} at ${fmtChipTime(p.starts_at)} a recurring stop on your weekly schedule? The original Google Calendar event stays untouched.`)) {
      return;
    }
    setConvertBusy(true);
    try {
      const { data } = await api.post(`/owner/gcal-events/${p.id}/convert-to-weekly`);
      if (data?.already_converted) {
        toast.info("Already on your weekly schedule");
      } else {
        toast.success("Promoted to weekly stop", {
          description: `Future ${dayLabel}s will show this stop automatically. The GCal pin won't duplicate.`,
        });
      }
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't convert to weekly");
    } finally {
      setConvertBusy(false);
    }
  };

  return (
    <div
      className={
        "mt-3 rounded-xl border px-3 py-2 text-sm " +
        (p.is_canceled
          ? "border-rose-400/40 bg-rose-500/5"
          : isGcal
            ? "border-primary/30 bg-primary/5"
            : "border-amber-500/30 bg-amber-500/5")
      }
      data-testid={`sched-oneoff-${dKey}-${p.id}`}
    >
      <div className="flex items-start gap-2">
        <span
          className={
            "mt-0.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
            (isGcal
              ? "bg-primary/10 text-primary"
              : "bg-amber-500/15 text-amber-700 dark:text-amber-300")
          }
        >
          {isGcal ? "📅 GCal" : "📍 One-off"}
        </span>
        <div className="flex-1 min-w-0">
          {p.is_canceled && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] rounded-full px-1.5 py-0.5 font-bold bg-rose-100 text-rose-700 border border-rose-300 mb-1"
              data-testid={`sched-chip-canceled-pill-${p.id}`}
              title={p.cancel_reason_custom || p.cancel_reason || "Event canceled"}
            >
              🚫 Canceled
            </span>
          )}
          <div className={`font-medium truncate ${p.is_canceled ? "text-rose-700 dark:text-rose-400 line-through" : "text-foreground"}`}>
            {p.event_name || p.address || "Untitled stop"}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {fmtChipTime(p.starts_at)}–{fmtChipTime(p.ends_at)}
            {p.event_name && p.address ? ` · ${p.address}` : ""}
          </div>
          {hasOverride && (
            <div className="mt-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wide">
              Pin manually set
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-none">
          {/* Cancel/Uncancel — shown for ALL chips (GCal + one-off).
              Inline so owners don't have to jump back to "Today's posts"
              just to mark a weather/mechanical cancellation. */}
          <button
            type="button"
            onClick={() => {
              setCancelForm({
                is_canceled: !!p.is_canceled,
                cancel_reason: p.cancel_reason || null,
                cancel_reason_custom: p.cancel_reason_custom || "",
              });
              setCancelOpen((v) => !v);
            }}
            className={
              "text-[11px] font-semibold whitespace-nowrap hover:underline " +
              (p.is_canceled ? "text-rose-600" : "text-rose-600/80")
            }
            data-testid={`sched-chip-cancel-toggle-${p.id}`}
          >
            {cancelOpen ? "Close" : (p.is_canceled ? "Uncancel" : "Cancel")}
          </button>
          {isGcal && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="text-[11px] font-semibold text-foreground hover:underline whitespace-nowrap"
              data-testid={`gcal-edit-${p.id}`}
              title="Edit this event in place (two-way syncs back to Google)"
            >
              Edit
            </button>
          )}
          {isGcal && (
            <button
              type="button"
              onClick={convertToWeekly}
              disabled={convertBusy}
              className="text-[11px] font-semibold text-emerald-600 hover:underline whitespace-nowrap disabled:opacity-50"
              data-testid={`gcal-make-weekly-${p.id}`}
              title="Promote this GCal event to a recurring weekly stop"
            >
              {convertBusy ? "Promoting…" : "Make weekly"}
            </button>
          )}
          {isGcal && (
            <button
              type="button"
              onClick={() => setEditing((v) => !v)}
              className="text-[11px] font-semibold text-primary hover:underline whitespace-nowrap"
              data-testid={`gcal-fix-pin-toggle-${p.id}`}
            >
              {editing ? "Cancel" : "Fix pin"}
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div
          className="mt-3 pt-3 border-t border-primary/20 space-y-2"
          data-testid={`gcal-fix-pin-panel-${p.id}`}
        >
          <Label className="text-xs">Drag the pin to the correct spot</Label>
          <ExpandablePinPicker
            lat={pin.lat}
            lng={pin.lng}
            onChange={(next) => setPin(next)}
            height={180}
            testIdPrefix={`gcal-pin-picker-${p.id}`}
          />
          <p className="text-[11px] text-muted-foreground leading-snug">
            Tap the map to expand fullscreen, then drop the pin where your
            truck actually parks. The override sticks across GCal syncs
            until you reset it.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={save}
              disabled={busy || pin.lat == null}
              data-testid={`gcal-fix-pin-save-${p.id}`}
            >
              {busy ? "Saving…" : "Save pin"}
            </Button>
            {hasOverride && (
              <button
                type="button"
                onClick={reset}
                disabled={busy}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid={`gcal-fix-pin-reset-${p.id}`}
              >
                Reset to GCal default
              </button>
            )}
            {pin.lat != null && (
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
              </span>
            )}
          </div>
        </div>
      )}
      {cancelOpen && (
        <div
          className="mt-3 pt-3 border-t border-rose-400/30 space-y-2"
          data-testid={`sched-chip-cancel-panel-${p.id}`}
        >
          <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card p-2.5 cursor-pointer select-none">
            <span className="min-w-0">
              <div className="text-xs font-semibold">Mark this event canceled</div>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                The pin stays on the map painted red until end time so customers know you didn&apos;t just forget.
              </div>
            </span>
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!cancelForm.is_canceled}
              onChange={(e) => setCancelForm((f) => ({
                ...f,
                is_canceled: e.target.checked,
                cancel_reason: e.target.checked ? f.cancel_reason : null,
                cancel_reason_custom: e.target.checked ? f.cancel_reason_custom : "",
              }))}
              data-testid={`sched-chip-cancel-checkbox-${p.id}`}
            />
            <span className="relative h-5 w-9 rounded-full bg-muted-foreground/30 peer-checked:bg-rose-500 transition-colors flex-none">
              <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${cancelForm.is_canceled ? "translate-x-4" : ""}`} />
            </span>
          </label>
          {cancelForm.is_canceled && (
            <div className="space-y-2">
              <Label className="text-xs">Cancellation reason</Label>
              <select
                value={cancelForm.cancel_reason || ""}
                onChange={(e) => setCancelForm((f) => ({
                  ...f,
                  cancel_reason: e.target.value || null,
                  cancel_reason_custom: e.target.value === "other" ? f.cancel_reason_custom : "",
                }))}
                className="w-full h-9 px-2 rounded-md border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                data-testid={`sched-chip-cancel-reason-${p.id}`}
              >
                <option value="">Pick a reason…</option>
                {CANCEL_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {cancelForm.cancel_reason === "other" && (
                <input
                  type="text"
                  maxLength={120}
                  value={cancelForm.cancel_reason_custom}
                  onChange={(e) => setCancelForm((f) => ({ ...f, cancel_reason_custom: e.target.value }))}
                  placeholder="e.g. Equipment failure"
                  className="w-full h-9 px-2 rounded-md border border-border bg-background text-xs"
                  data-testid={`sched-chip-cancel-custom-${p.id}`}
                />
              )}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={saveCancel}
              disabled={cancelBusy || (cancelForm.is_canceled && !cancelForm.cancel_reason)}
              className={cancelForm.is_canceled ? "bg-rose-600 hover:bg-rose-700 text-white" : ""}
              data-testid={`sched-chip-cancel-save-${p.id}`}
            >
              <Prohibit weight="duotone" className="h-4 w-4 mr-1" />
              {cancelBusy ? "Saving…" : (cancelForm.is_canceled ? "Mark canceled" : "Uncancel event")}
            </Button>
          </div>
        </div>
      )}
      <EventEditDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        event={drawerOpen ? p : null}
        onSaved={() => onChanged?.()}
      />
    </div>
  );
}


/**
 * Truck Calendar — Operations panel (Feb 27 2026 / Phase 1A).
 *
 * Always-visible visibility chip (Public / Staff / Private) on every
 * entry so the customer-map status is glanceable, plus a "Show ops
 * detail" toggle that reveals event type + prep count + prep notes
 * + staff needed + ops notes. Designed to stay collapsed by default
 * so simple week-planning isn't cluttered for owners who don't need
 * the ops layer yet.
 *
 * Props:
 *   dKey   — weekday slug ("monday", "tuesday", …) used for testid + label.
 *   idx    — index inside `sched.days[dKey]` (drives unique testids).
 *   entry  — the in-memory entry object (`sched.days[dKey][idx]`).
 *   onPatch(partial) — merges `partial` into `entry` via the parent's
 *     `updateEntry` so all ops changes flow through the normal "Save
 *     this week's schedule" button (no separate save).
 *
 * Persistence: zero local API calls. The parent `ScheduleEditor.onSave`
 * sends the entire schedule (including ops fields) on save, and the
 * backend's `_clean_schedule_days` runs every entry through
 * `_coerce_ops_fields` to sanitize visibility + clamp numeric fields.
 */
function OpsPanel({ dKey, idx, entry, onPatch }) {
  const [open, setOpen] = useState(false);
  const vis = VISIBILITY_OPTIONS.find((v) => v.value === (entry.visibility || "public"))
    || VISIBILITY_OPTIONS[0];
  return (
    <div
      className="rounded-lg border border-dashed border-border/60 bg-card/40 px-2.5 py-2 space-y-2"
      data-testid={`sched-entry-ops-${dKey}-${idx}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${vis.accent}`}
            data-testid={`sched-entry-vis-pill-${dKey}-${idx}`}
            title={vis.hint}
          >
            {vis.value === "public" ? "🌐" : vis.value === "staff" ? "👥" : "🔒"}
            {vis.label}
          </span>
          {entry.event_type && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground hidden sm:inline">
              {(EVENT_TYPE_OPTIONS.find((e) => e.value === entry.event_type) || {}).label}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-primary hover:underline whitespace-nowrap"
          data-testid={`sched-entry-ops-toggle-${dKey}-${idx}`}
        >
          {open ? "Hide details" : "Show ops detail"}
        </button>
      </div>
      {open && (
        <div className="space-y-2 pt-1" data-testid={`sched-entry-ops-panel-${dKey}-${idx}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Who sees this
              </Label>
              <select
                value={entry.visibility || "public"}
                onChange={(e) => onPatch({ visibility: e.target.value })}
                className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-xs"
                data-testid={`sched-entry-visibility-${dKey}-${idx}`}
              >
                {VISIBILITY_OPTIONS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-muted-foreground mt-1 leading-snug">
                {vis.hint}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Event type
              </Label>
              <select
                value={entry.event_type || ""}
                onChange={(e) => onPatch({ event_type: e.target.value || null })}
                className="mt-1 w-full h-9 px-2 rounded-md border border-border bg-background text-xs"
                data-testid={`sched-entry-event-type-${dKey}-${idx}`}
              >
                <option value="">—</option>
                {EVENT_TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Prep count
              </Label>
              <Input
                type="number"
                min={0}
                value={entry.prep_count ?? ""}
                onChange={(e) => onPatch({ prep_count: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                placeholder="e.g. 200"
                className="mt-1 h-9 text-xs"
                data-testid={`sched-entry-prep-count-${dKey}-${idx}`}
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Staff needed
              </Label>
              <Input
                type="number"
                min={0}
                value={entry.staff_needed ?? ""}
                onChange={(e) => onPatch({ staff_needed: e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0) })}
                placeholder="e.g. 3"
                className="mt-1 h-9 text-xs"
                data-testid={`sched-entry-staff-needed-${dKey}-${idx}`}
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Prep notes
            </Label>
            <textarea
              value={entry.prep_notes || ""}
              onChange={(e) => onPatch({ prep_notes: e.target.value })}
              placeholder="Burger patties, fries, slaw, drinks…"
              rows={2}
              className="mt-1 w-full px-2 py-1.5 rounded-md border border-border bg-background text-xs"
              data-testid={`sched-entry-prep-notes-${dKey}-${idx}`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Ops notes (private)
            </Label>
            <textarea
              value={entry.ops_notes || ""}
              onChange={(e) => onPatch({ ops_notes: e.target.value })}
              placeholder="Gate code, parking pass, organizer contact…"
              rows={2}
              className="mt-1 w-full px-2 py-1.5 rounded-md border border-border bg-background text-xs"
              data-testid={`sched-entry-ops-notes-${dKey}-${idx}`}
            />
          </div>
          {entry.visibility !== "public" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300">
              {entry.visibility === "private"
                ? "🔒 This entry is owner-only — it will not appear on your customer map."
                : "👥 This entry is staff-only — customers can't see it, but invited staff can (coming in Phase 1B)."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



export default function ScheduleEditor({ truckId, onDeleted }) {
  const [sched, setSched] = useState(emptySchedule());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geocodingId, setGeocodingId] = useState(null); // `${day}-${idx}`
  // Which day rows have their "Override for this day" images expanded
  const [overrideOpen, setOverrideOpen] = useState(() => new Set());
  // Which week is the owner editing? Defaults to current week. Changing
  // this re-fetches the truck's schedule for that specific week — nothing
  // carries forward automatically (user's explicit spec).
  const [weekStart, setWeekStart] = useState(() => defaultPlanningWeekStart());
  // One-off posts (db.trucks rows) for THIS truck within the selected
  // week. Includes Google-Calendar-synced events. Rendered read-only
  // inside each day card so owners can see "this Tuesday is *also* my
  // GCal booking at 5pm" alongside their recurring schedule.
  const [oneOffsByDay, setOneOffsByDay] = useState({});

  const endpoint = truckId ? `/schedules/${truckId}` : "/schedule";

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(endpoint, { params: { week_start: weekStart } })
      .then(({ data }) => {
        if (!mounted) return;
        const days = {};
        DAYS.forEach((d) => {
          const raw = data.days?.[d.key];
          days[d.key] = Array.isArray(raw)
            ? raw.map((e) => ({ ...e, menu_image_paths: Array.isArray(e.menu_image_paths) ? e.menu_image_paths : [] }))
            : [];
        });
        const weeklyPaths = Array.isArray(data.menu_image_paths) && data.menu_image_paths.length
          ? data.menu_image_paths
          : (data.menu_image_path ? [data.menu_image_path] : []);
        setSched({
          truck_name: data.truck_name || "",
          menu_text: data.menu_text || "",
          menu_image_paths: weeklyPaths,
          food_category: data.food_category || "default",
          days,
        });
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [endpoint, weekStart]);

  // Bumping this triggers a re-fetch of one-offs (used after pin-override
  // mutations so the chip shows the corrected address without a page reload).
  const [oneOffReloadKey, setOneOffReloadKey] = useState(0);
  const refreshOneOffs = () => setOneOffReloadKey((n) => n + 1);

  // Load one-off posts (incl. Google-Calendar-synced) for this truck
  // within the selected week. Bucket by day of week for inline display
  // alongside the recurring weekly schedule entries.
  useEffect(() => {
    let mounted = true;
    api
      .get("/trucks/mine")
      .then(({ data }) => {
        if (!mounted) return;
        // weekStart is a 'YYYY-MM-DD' string. Build [weekStart, +7d) range.
        const weekEndDate = new Date(weekStart + "T12:00:00Z");
        weekEndDate.setUTCDate(weekEndDate.getUTCDate() + 7);
        const weekEndKey = weekEndDate.toISOString().slice(0, 10);
        const dayKeys = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
        const buckets = Object.fromEntries(DAYS.map((d) => [d.key, []]));
        for (const t of data || []) {
          if (truckId && t.truck_id && t.truck_id !== truckId) continue;
          // ET calendar date, so bucketing matches the truck's wall clock
          // no matter what timezone the owner's device is set to.
          const etKey = etDateKey(t.starts_at);
          if (!etKey) continue;
          if (etKey < weekStart || etKey >= weekEndKey) continue;
          const key = dayKeys[etWeekdayIndex(t.starts_at)];
          if (buckets[key]) buckets[key].push(t);
        }
        // Sort each day's bucket chronologically.
        for (const k of Object.keys(buckets)) {
          buckets[k].sort((a, b) => etTimeMs(a.starts_at) - etTimeMs(b.starts_at));
        }
        setOneOffsByDay(buckets);
      })
      .catch((e) => {
        console.error("one-off bucketing failed", e);
        if (mounted) setOneOffsByDay({});
      });
    return () => { mounted = false; };
  }, [truckId, weekStart, oneOffReloadKey]);

  // Quick helper to format a one-off entry's time range — keeps the
  // chip compact: "11a-2p"-style rather than "11:00 AM — 2:00 PM".
  const fmtChipTime = (iso) => {
    const s = fmtET(iso, { hour: "numeric", minute: "2-digit" }); // "11:00 AM"
    if (!s) return iso;
    return s.replace(":00", "").replace(/\s?AM/i, "a").replace(/\s?PM/i, "p");
  };

  const setDayEntries = (key, entries) =>
    setSched((s) => ({ ...s, days: { ...s.days, [key]: entries } }));

  const addEntry = (key) =>
    setDayEntries(key, [...(sched.days[key] || []), emptyEntry()]);

  const updateEntry = (key, idx, patch) => {
    const next = [...sched.days[key]];
    next[idx] = { ...next[idx], ...patch };
    setDayEntries(key, next);
  };

  const removeEntry = (key, idx) => {
    const next = sched.days[key].filter((_, i) => i !== idx);
    setDayEntries(key, next);
  };

  const onGeocodeEntry = async (key, idx) => {
    const entry = sched.days[key][idx];
    if (!entry.address) return;
    const id = `${key}-${idx}`;
    // Coordinates pasted instead of an address (Google Maps copy-paste)?
    // Pin the exact spot and reverse-geocode a display address.
    const coords = parseLatLng(entry.address);
    if (coords) {
      setGeocodingId(id);
      const r = await reverseGeocode(coords.lat, coords.lng);
      setGeocodingId(null);
      if (r?.address) {
        updateEntry(key, idx, {
          address: r.address, lat: coords.lat, lng: coords.lng,
          geocode_warning: null, pin_source: "coordinates",
        });
        toast.success("Coordinates pinned — address filled in", { description: r.address });
      } else {
        updateEntry(key, idx, {
          lat: coords.lat, lng: coords.lng,
          geocode_warning: null, pin_source: "coordinates",
        });
        toast.message("Pinned your exact coordinates", {
          description:
            "No street address found for that spot — type a short description in the address box (the pin keeps your coordinates).",
          duration: 10000,
        });
      }
      return;
    }
    setGeocodingId(id);
    const r = await geocodeAddress(entry.address);
    setGeocodingId(null);
    if (!r) {
      toast.error("Couldn't find that address — drop the pin manually below");
      return;
    }
    // NEW (May 2026): hard-reject anything short of a full street
    // address with ZIP code. The owner gets a clear inline error +
    // an obvious "Drop pin manually" path via the fullscreen map.
    // Don't auto-pin a low-quality match — that's what produced the
    // off-by-a-block pins on production.
    if (r.is_full_address === false) {
      const missing = (r.missing_address_parts || []).join(", ") || "a complete address";
      toast.error(
        `Address is missing ${missing}. Type the full address including ZIP — or drop the pin manually below.`,
        { duration: 7000 },
      );
      // Clear any stale lat/lng so the inline picker switches into the
      // "drop a pin" state and the Save button stays gated.
      updateEntry(key, idx, {
        lat: null, lng: null,
        geocode_warning: `Missing ${missing}. Type a full address (with ZIP) or drop the pin manually.`,
      });
      return;
    }
    updateEntry(key, idx, { lat: r.lat, lng: r.lng, geocode_warning: null });
    if (r.approximate) {
      toast.message("Pinned approximate location (zip-level match)");
    } else {
      toast.success("Location pinned");
    }
  };

  const applyMondayToAll = () => {
    const mon = sched.days.monday || [];
    if (mon.length === 0) {
      toast.error("Add at least one event to Monday first");
      return;
    }
    if (mon.some((e) => e.lat == null || e.lng == null)) {
      toast.error("Pin every Monday event (click Find) before copying");
      return;
    }
    setSched((s) => {
      const next = { ...s.days };
      DAYS.forEach((d) => {
        if (d.key !== "monday") {
          // Copy structural fields only — cancellation is week+day
          // specific, so a canceled Monday stop should NOT propagate
          // its red flag to Tuesday through Sunday.
          next[d.key] = mon.map((e) => ({
            ...e,
            menu_image_paths: [],
            is_canceled: false,
            cancel_reason: null,
            cancel_reason_custom: "",
          }));
        }
      });
      return { ...s, days: next };
    });
    toast.success("Monday's events copied to all 7 days");
  };

  const toggleOverride = (key) => {
    setOverrideOpen((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Fire-and-forget pin-accuracy check. Flags any saved entry whose pin
  // is 10+ miles from its typed address (server uses Nominatim, caches
  // per-address for 24 h). Best-effort — never surfaces errors; if the
  // network/geocoder hiccups we silently skip.
  const _checkPinsInBackground = async (snapshot) => {
    const entries = [];
    DAYS.forEach((d) => {
      const list = snapshot.days?.[d.key] || [];
      list.forEach((e) => {
        if (e?.address && e?.lat != null && e?.lng != null) {
          entries.push({ day: d.key, address: e.address, lat: e.lat, lng: e.lng });
        }
      });
    });
    if (entries.length === 0) return;
    try {
      const { data } = await api.post("/pin-check", { entries });
      const flagged = data?.suspicious || [];
      if (flagged.length === 0) return;
      // Dedup by address so we don't double-warn when the same spot is
      // on multiple days. Show the worst offender + count of others.
      const worst = flagged.reduce((a, b) => (b.miles > a.miles ? b : a));
      const rest = flagged.length - 1;
      toast.warning(
        rest > 0
          ? `Pin looks ${worst.miles}mi from "${worst.address}" (+ ${rest} more). Drag to fix?`
          : `Pin looks ${worst.miles}mi from "${worst.address}". Drag to fix?`,
        { duration: 8000 }
      );
    } catch {
      /* swallow — pin-check is best-effort */
    }
  };

  const onSave = async () => {
    if (!sched.truck_name.trim()) {
      toast.error("Truck name is required");
      return;
    }
    // NEW (May 2026): block save when any event has an address but no
    // pinned lat/lng (i.e. the geocode was rejected for incomplete
    // info, or the owner hasn't dropped a pin yet). This is the
    // client-side mirror of the server-side full-address gate — keeps
    // the failure mode obvious instead of letting the server return a
    // 400 after a "Saving…" spinner.
    const unpinned = [];
    DAYS.forEach((d) => {
      (sched.days[d.key] || []).forEach((e, idx) => {
        if (e.address && (e.lat == null || e.lng == null)) {
          unpinned.push(`${d.label} #${idx + 1}`);
        }
      });
    });
    if (unpinned.length) {
      toast.error(
        `Pin missing on: ${unpinned.slice(0, 3).join(", ")}${unpinned.length > 3 ? "…" : ""}. ` +
          "Use the full address with ZIP, or drop the pin manually on the map.",
        { duration: 7000 },
      );
      return;
    }
    setSaving(true);
    try {
      await api.put(endpoint, { ...sched, week_start: weekStart });
      toast.success("Schedule saved for this week");
      // Non-blocking pin-accuracy check. Fire-and-forget so save feels
      // instant; warn-only toast if any pin sits 10+ miles from its typed
      // address (common mistake: dragging the pin into a cornfield).
      _checkPinsInBackground(sched);
      // Re-fetch so any server-side auto-geocoded coords appear as ✓ in the UI.
      const { data } = await api.get(endpoint, { params: { week_start: weekStart } });
      const days = {};
      DAYS.forEach((d) => {
        const raw = data.days?.[d.key];
        days[d.key] = Array.isArray(raw)
          ? raw.map((e) => ({ ...e, menu_image_paths: Array.isArray(e.menu_image_paths) ? e.menu_image_paths : [] }))
          : [];
      });
      const weeklyPaths = Array.isArray(data.menu_image_paths) && data.menu_image_paths.length
        ? data.menu_image_paths
        : (data.menu_image_path ? [data.menu_image_path] : []);
      setSched({
        truck_name: data.truck_name || "",
        menu_text: data.menu_text || "",
        menu_image_paths: weeklyPaths,
        food_category: data.food_category || "default",
        days,
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onDeleteTruck = async () => {
    if (!truckId) return;
    if (!window.confirm(`Delete "${sched.truck_name || "this truck"}" and every week's schedule for it?`)) return;
    try {
      await api.delete(`/schedules/${truckId}`);
      toast.success("Truck deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totalEvents = useMemo(
    () => DAYS.reduce((acc, d) => acc + (sched.days[d.key]?.length || 0), 0),
    [sched.days],
  );

  if (loading) return <div className="text-muted-foreground py-10">Loading schedule…</div>;

  return (
    <div className="space-y-6" data-testid="schedule-editor">
      {/* Pro-only: AI flyer-to-schedule auto-fill. Hidden for non-Pro. */}
      <FlyerExtractCard
        onDaysExtracted={(filledDays) => {
          // Normalize to the shape the editor expects (menu_image_paths array).
          setSched((s) => {
            const next = { ...s.days };
            for (const d of Object.keys(filledDays)) {
              next[d] = (filledDays[d] || []).map((e) => ({
                event_name: e.event_name || "",
                address: e.address || "",
                lat: e.lat ?? null,
                lng: e.lng ?? null,
                starts_time: e.starts_time || "11:00",
                ends_time: e.ends_time || "14:00",
                menu_image_paths: Array.isArray(e.menu_image_paths) ? e.menu_image_paths : [],
                recurring: !!e.recurring,
              }));
            }
            return { ...s, days: next };
          });
        }}
        onTruckNameSuggested={(name) => {
          setSched((s) => (s.truck_name?.trim() ? s : { ...s, truck_name: name }));
        }}
      />

      {/* Week selector — schedules are per-week, not recurring. */}
      <WeekSelector value={weekStart} onChange={setWeekStart} disabled={saving} />
      <div className="rounded-xl border border-dashed border-border bg-muted/20 px-4 py-2.5 text-xs text-muted-foreground" data-testid="week-helper-banner">
        <span className="font-semibold text-foreground">Events save for this week only.</span> To plan next week, pick a future week above and add events there. Nothing repeats automatically.
      </div>

      {/* First-time owner nudge — soft banner that only appears when the truck
          has no food category set. Auto-dismisses once they pick one
          (driven by sched.food_category), so there's no explicit dismiss
          state to manage. "Pick now" scrolls to + focuses the select. */}
      {(!sched.food_category || sched.food_category === "default") && (
        <div
          className="rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 flex items-start gap-3"
          data-testid="category-nudge-banner"
        >
          <div className="h-8 w-8 rounded-full bg-primary/15 grid place-items-center flex-none text-lg" aria-hidden="true">
            🍔
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-foreground">
              Pick a food category so customers can find you
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              TruckTrackr only shows the category filter bar once enough trucks have one. Choosing yours makes you easier to discover — and helps unlock filters for everyone.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("sch-food-category");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => el.focus(), 400);
              }
            }}
            className="flex-none h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
            data-testid="category-nudge-cta"
          >
            Pick now
          </button>
        </div>
      )}
      {/* Top: truck name + menu */}
      <div className="rounded-2xl border border-border bg-card p-5 space-y-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="sch-name">Truck name</Label>
            <Input
              id="sch-name"
              value={sched.truck_name}
              onChange={(e) => setSched({ ...sched, truck_name: e.target.value })}
              placeholder="e.g. Pepe's Tacos"
              data-testid="sched-truck-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sch-menu">Menu text (optional)</Label>
            <Textarea
              id="sch-menu"
              rows={2}
              value={sched.menu_text}
              onChange={(e) => setSched({ ...sched, menu_text: e.target.value })}
              placeholder="What do you serve?"
              data-testid="sched-menu-text"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="sch-food-category">Map icon / food type</Label>
          <div className="flex items-center gap-3">
            {/* Live preview of the pin so the owner sees exactly what customers
                will see on the map. */}
            <div
              className="h-11 w-11 grid place-items-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-lg shadow-sm flex-none"
              aria-hidden="true"
              data-testid="sched-food-category-preview"
            >
              {getCategoryOrDefault(sched.food_category).emoji || (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6h11v10H3zM14 9h4l3 3v4h-7z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
                  <circle cx="7" cy="17" r="2" fill="white" />
                  <circle cx="17" cy="17" r="2" fill="white" />
                </svg>
              )}
            </div>
            <select
              id="sch-food-category"
              value={sched.food_category || "default"}
              onChange={(e) => setSched({ ...sched, food_category: e.target.value })}
              className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="sched-food-category-select"
            >
              {FOOD_CATEGORIES.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.emoji ? `${c.emoji}  ${c.label}` : c.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-muted-foreground">
            Choose the icon that best represents what your truck serves. This helps customers find you faster.
          </p>
        </div>

        <div className="rounded-xl border border-dashed border-border p-4 bg-background/50">
          <div className="flex items-start gap-2 mb-3">
            <ImagesSquare weight="duotone" className="h-5 w-5 text-primary flex-none mt-0.5" />
            <div>
              <div className="font-medium text-sm">Weekly menu photos</div>
              <div className="text-xs text-muted-foreground">
                Default for all days (up to 3). Add per-day overrides below to
                replace these on a specific day.
              </div>
            </div>
          </div>
          <MenuImagesEditor
            paths={sched.menu_image_paths}
            onChange={(paths) => setSched((s) => ({ ...s, menu_image_paths: paths }))}
            testId="weekly-images"
          />
        </div>
      </div>

      {/* Quick action */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          <span className="font-display font-semibold text-foreground">{totalEvents}</span> event
          {totalEvents === 1 ? "" : "s"} across the week
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={applyMondayToAll}
          className="gap-2"
          data-testid="apply-monday-all-btn"
        >
          <CopySimple weight="duotone" className="h-4 w-4" />
          Copy Monday to all days
        </Button>
      </div>

      {/* Day cards */}
      <div className="space-y-3">
        {DAYS.map((d) => {
          const entries = sched.days[d.key] || [];
          const oneOffs = oneOffsByDay[d.key] || [];
          return (
            <div
              key={d.key}
              className="rounded-2xl border border-border bg-card p-4 sm:p-5"
              data-testid={`sched-day-${d.key}`}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-display font-semibold text-base tracking-tight">
                  {d.label}
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {entries.length === 0 && oneOffs.length === 0
                      ? "closed"
                      : `${entries.length + oneOffs.length} event${
                          entries.length + oneOffs.length === 1 ? "" : "s"
                        }`}
                  </span>
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => addEntry(d.key)}
                  className="gap-1 text-primary hover:text-primary"
                  data-testid={`sched-add-${d.key}`}
                >
                  <Plus weight="bold" className="h-4 w-4" /> Add event
                </Button>
              </div>

              {/* Read-only chips for one-off posts + Google-Calendar
                  synced events that fall on this day inside the selected
                  week. Not editable here — owners edit synced events
                  in Google Calendar; manual one-offs edit on Today's
                  posts. This view is just so you SEE the day's full
                  picture alongside recurring entries. */}
              {oneOffs.map((p) => {
                const isGcal = p.source === "google_calendar";
                return (
                  <GcalChip
                    key={`oneoff-${p.id}`}
                    p={p}
                    isGcal={isGcal}
                    dKey={d.key}
                    fmtChipTime={fmtChipTime}
                    onChanged={refreshOneOffs}
                  />
                );
              })}

              <AnimatePresence initial={false}>
                {entries.map((entry, idx) => {
                  const gid = `${d.key}-${idx}`;
                  return (
                    <motion.div
                      key={gid}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.18 }}
                      className="overflow-hidden"
                    >
                      <div
                        className={
                          "mt-3 rounded-xl border p-3 space-y-2 " +
                          (entry.is_canceled
                            ? "border-rose-400/40 bg-rose-500/5"
                            : "border-border bg-background")
                        }
                        data-testid={`sched-entry-${d.key}-${idx}`}
                      >
                        {entry.is_canceled && (
                          <div
                            className="-mt-1 mb-1 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-rose-700"
                            data-testid={`sched-entry-canceled-pill-${d.key}-${idx}`}
                          >
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-rose-100 border border-rose-300">
                              🚫 Canceled
                            </span>
                            {entry.cancel_reason && (
                              <span className="text-[10px] tracking-wider text-rose-600/80 normal-case">
                                · {CANCEL_REASONS.find((r) => r.value === entry.cancel_reason)?.label || entry.cancel_reason}
                                {entry.cancel_reason === "other" && entry.cancel_reason_custom
                                  ? `: ${entry.cancel_reason_custom}`
                                  : ""}
                              </span>
                            )}
                          </div>
                        )}
                        <Input
                          value={entry.event_name || ""}
                          onChange={(e) => updateEntry(d.key, idx, { event_name: e.target.value })}
                          placeholder="Event name (optional) — e.g. Ephrata Agway"
                          className="font-display font-semibold"
                          data-testid={`sched-event-name-${d.key}-${idx}`}
                        />
                        {/* Middle (time) column is `auto`, not a fixed px
                            width — native time inputs have a wide intrinsic
                            size and were overflowing under the trash button
                            (real owner bug report, Oct 2026). */}
                        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_44px] gap-2 items-center">
                          <div className="flex gap-2">
                            <Input
                              value={entry.address}
                              onChange={(e) =>
                                updateEntry(
                                  d.key, idx,
                                  entry.pin_source === "coordinates"
                                    ? { address: e.target.value }
                                    : { address: e.target.value, lat: null, lng: null }
                                )
                              }
                              placeholder="Address — or paste coordinates (40.03, -76.30)"
                              data-testid={`sched-address-${d.key}-${idx}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!entry.address || geocodingId === gid}
                              onClick={() => onGeocodeEntry(d.key, idx)}
                              className="gap-1 flex-none"
                              data-testid={`sched-find-${d.key}-${idx}`}
                            >
                              <MapPinSimple weight="duotone" className="h-4 w-4" />
                              {geocodingId === gid ? "…" : entry.lat != null ? "✓" : "Find"}
                            </Button>
                          </div>

                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={entry.starts_time}
                              onChange={(e) => updateEntry(d.key, idx, { starts_time: e.target.value })}
                              data-testid={`sched-start-${d.key}-${idx}`}
                            />
                            <span className="text-muted-foreground text-xs">to</span>
                            <Input
                              type="time"
                              value={entry.ends_time}
                              onChange={(e) => updateEntry(d.key, idx, { ends_time: e.target.value })}
                              data-testid={`sched-end-${d.key}-${idx}`}
                            />
                          </div>

                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeEntry(d.key, idx)}
                            aria-label="Remove event"
                            data-testid={`sched-remove-${d.key}-${idx}`}
                          >
                            <Trash weight="duotone" className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>

                        {/* Repeat every week toggle — when on, this stop
                            auto-rolls into future weeks (until owner edits
                            or removes it for that week). Saves owners
                            who park at the same lot every Wednesday from
                            retyping the entry every Monday. */}
                        <label
                          className="flex items-center gap-2 text-xs cursor-pointer select-none"
                          data-testid={`sched-recurring-label-${d.key}-${idx}`}
                        >
                          <input
                            type="checkbox"
                            checked={!!entry.recurring}
                            onChange={(e) => updateEntry(d.key, idx, { recurring: e.target.checked })}
                            className="h-4 w-4 rounded border-border accent-primary"
                            data-testid={`sched-recurring-${d.key}-${idx}`}
                          />
                          <span className="font-medium">Repeat every {d.label}</span>
                          {entry.recurring && (
                            <span className="text-[10px] uppercase tracking-wider font-bold text-primary">
                              Recurring
                            </span>
                          )}
                        </label>

                        {/* Truck Calendar — Operations panel (Phase 1A).
                            Always shows the visibility chip (so the
                            "this is private" status is glanceable),
                            with the rest of the ops fields tucked
                            behind a "Show details" toggle. Keeps the
                            schedule editor clean for the 80% case
                            while giving owners a real ops back-end
                            when they want it. Saves via the normal
                            "Save this week's schedule" button below. */}
                        <OpsPanel
                          dKey={d.key}
                          idx={idx}
                          entry={entry}
                          onPatch={(patch) => updateEntry(d.key, idx, patch)}
                        />

                        {/* Per-entry cancellation. Stays week-specific —
                            when this stop has `recurring=true`, the cancel
                            flag intentionally does NOT roll forward (see
                            `_lift_recurring_days` on the backend), so a
                            "this Saturday's Target stop is canceled"
                            won't accidentally hide next Saturday's. The
                            toggle persists via the normal "Save this
                            week's schedule" button below — same save
                            flow as everything else on the page. */}
                        <div
                          className={
                            "rounded-lg border px-2.5 py-2 " +
                            (entry.is_canceled
                              ? "border-rose-400/40 bg-rose-500/5"
                              : "border-dashed border-border/60 bg-card/40")
                          }
                          data-testid={`sched-entry-cancel-row-${d.key}-${idx}`}
                        >
                          <label className="flex items-center justify-between gap-2 cursor-pointer select-none">
                            <span className="flex items-center gap-2 min-w-0">
                              <Prohibit
                                weight={entry.is_canceled ? "fill" : "duotone"}
                                className={"h-4 w-4 flex-none " + (entry.is_canceled ? "text-rose-600" : "text-muted-foreground")}
                              />
                              <span className="text-xs font-medium">
                                {entry.is_canceled ? "This stop is canceled" : "Cancel this stop"}
                              </span>
                              {!entry.is_canceled && (
                                <span className="text-[10px] text-muted-foreground hidden sm:inline">
                                  · pin stays red on the map (not erased)
                                </span>
                              )}
                            </span>
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={!!entry.is_canceled}
                              onChange={(e) => updateEntry(d.key, idx, {
                                is_canceled: e.target.checked,
                                cancel_reason: e.target.checked ? entry.cancel_reason : null,
                                cancel_reason_custom: e.target.checked ? entry.cancel_reason_custom : "",
                              })}
                              data-testid={`sched-entry-cancel-toggle-${d.key}-${idx}`}
                            />
                            <span className="relative h-5 w-9 rounded-full bg-muted-foreground/30 peer-checked:bg-rose-500 transition-colors flex-none">
                              <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${entry.is_canceled ? "translate-x-4" : ""}`} />
                            </span>
                          </label>
                          {entry.is_canceled && (
                            <div className="mt-2 space-y-2">
                              <select
                                value={entry.cancel_reason || ""}
                                onChange={(e) => updateEntry(d.key, idx, {
                                  cancel_reason: e.target.value || null,
                                  cancel_reason_custom: e.target.value === "other" ? entry.cancel_reason_custom : "",
                                })}
                                className="w-full h-9 px-2 rounded-md border border-border bg-background text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/40"
                                data-testid={`sched-entry-cancel-reason-${d.key}-${idx}`}
                              >
                                <option value="">Pick a reason…</option>
                                {CANCEL_REASONS.map((r) => (
                                  <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                              </select>
                              {entry.cancel_reason === "other" && (
                                <Input
                                  type="text"
                                  maxLength={120}
                                  value={entry.cancel_reason_custom || ""}
                                  onChange={(e) => updateEntry(d.key, idx, { cancel_reason_custom: e.target.value })}
                                  placeholder="e.g. Equipment failure"
                                  className="h-9 text-xs"
                                  data-testid={`sched-entry-cancel-custom-${d.key}-${idx}`}
                                />
                              )}
                              <div className="text-[10px] text-muted-foreground">
                                Don&apos;t forget to hit <strong>Save this week&apos;s schedule</strong> below to publish the cancellation.
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Pin-drop map. Always render when the entry
                            has an address — even without lat/lng yet,
                            since the user might need to drop a pin
                            manually for a location with no street
                            number (festival lot, park, etc.). Clicking
                            anywhere on the small map opens the new
                            fullscreen picker. */}
                        {entry.address && (
                          <div className="space-y-1">
                            {entry.geocode_warning ? (
                              <div
                                className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
                                role="alert"
                                data-testid={`sched-geocode-warning-${d.key}-${idx}`}
                              >
                                <Warning weight="duotone" className="h-4 w-4 flex-none mt-0.5" />
                                <span>{entry.geocode_warning}</span>
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                {entry.lat != null && entry.lng != null
                                  ? "Pin looks wrong? Tap the map to fine-tune on a bigger view."
                                  : "Drop a pin to mark the exact spot — opens a fullscreen map."}
                              </p>
                            )}
                            <ExpandablePinPicker
                              lat={entry.lat}
                              lng={entry.lng}
                              onChange={({ lat, lng }) =>
                                updateEntry(d.key, idx, {
                                  lat, lng,
                                  // Manual pin drop is the escape hatch
                                  // for the strict address rule — clearing
                                  // the warning unblocks Save.
                                  geocode_warning: null,
                                })
                              }
                              testIdPrefix={`sched-pin-${d.key}-${idx}`}
                            />
                          </div>
                        )}

                        {/* Per-day menu image override */}
                        {(() => {
                          const okey = `${d.key}-${idx}`;
                          const open = overrideOpen.has(okey);
                          const hasImages = (entry.menu_image_paths || []).length > 0;
                          return (
                            <div className="pt-1">
                              <button
                                type="button"
                                onClick={() => toggleOverride(okey)}
                                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition"
                                data-testid={`sched-toggle-override-${d.key}-${idx}`}
                              >
                                <CaretDown
                                  weight="bold"
                                  className={`h-3 w-3 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
                                />
                                Override menu photos for this event
                                {hasImages && (
                                  <span className="ml-1 text-[10px] uppercase tracking-wider text-primary bg-primary/15 rounded-full px-1.5 py-0.5 font-semibold">
                                    {entry.menu_image_paths.length}
                                  </span>
                                )}
                              </button>
                              {open && (
                                <div className="mt-2 rounded-lg border border-dashed border-border p-3 bg-card">
                                  <div className="text-[11px] text-muted-foreground mb-2">
                                    Override for this day only — these replace the weekly photos.
                                  </div>
                                  <MenuImagesEditor
                                    paths={entry.menu_image_paths || []}
                                    onChange={(paths) => updateEntry(d.key, idx, { menu_image_paths: paths })}
                                    testId={`day-images-${d.key}-${idx}`}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3">
        {truckId ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDeleteTruck}
            className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="sched-delete-truck-btn"
          >
            <Trash weight="duotone" className="h-4 w-4" /> Delete truck
          </Button>
        ) : (
          <div />
        )}
        <Button
          onClick={onSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-11 px-6"
          data-testid="sched-save-btn"
        >
          {saving ? "Saving…" : "Save this week's schedule"}
        </Button>
      </div>
    </div>
  );
}
